import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function LawyerDashboard() {
  const navigate = useNavigate();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    client_id: "",
    title: "",
    message: "",
  });
  const [requestDocuments, setRequestDocuments] = useState([]);
  const [requestSuccess, setRequestSuccess] = useState("");
  const [requestError, setRequestError] = useState("");
  const [clients, setClients] = useState([]);
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    country: "",
    specialization: "",
    bio: "",
    image: null,
  });
  const [success, setSuccess] = useState("");

  // Fetch dashboard data and clients
  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("You are not logged in.");
          setLoading(false);
          return;
        }
        // Fetch profile
        const profileRes = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyer/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const profileData = await profileRes.json();

        // Fetch requests
        const requestsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyer/requests`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const requestsData = await requestsRes.json();

        if (profileRes.ok && requestsRes.ok) {
          setProfile(profileData.profile || profileData);
          setRequests(requestsData.requests || []);
        } else {
          setError(profileData.message || requestsData.message || "Failed to load dashboard.");
        }
      } catch {
        setError("Network error.");
      }
      setLoading(false);
    };

    const fetchClients = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/clients`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const data = await res.json();
        if (res.ok) setClients(data.clients || []);
      } catch {}
    };

    fetchDashboard();
    fetchClients();
  }, []);

  // Handle request form changes
  const handleRequestChange = (e) => {
    const { name, value } = e.target;
    setRequestForm({ ...requestForm, [name]: value });
  };

  const handleRequestFileChange = (e) => {
    setRequestDocuments([...e.target.files]);
  };

  // Handle sending request to client
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setRequestSuccess("");
    setRequestError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setRequestError("You are not logged in.");
        return;
      }
      const formData = new FormData();
      formData.append("client_id", requestForm.client_id);
      formData.append("title", requestForm.title);
      formData.append("message", requestForm.message);
      requestDocuments.forEach((file) => {
        formData.append("documents[]", file);
      });

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyer/request-client`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setRequestSuccess("Request sent successfully!");
        setShowRequestModal(false);
        setRequestForm({ client_id: "", title: "", message: "" });
        // Optionally, refetch requests
        const requestsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyer/requests`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const requestsData = await requestsRes.json();
        if (requestsRes.ok) setRequests(requestsData.requests || []);
      } else {
        setRequestError(data.message || "Failed to send request.");
      }
    } catch {
      setRequestError("Network error.");
    }
  };

  // Start editing profile
  const handleEdit = () => {
    setEditForm({
      name: profile.name || "",
      phone: profile.phone || "",
      address: profile.address || "",
      city: profile.city || "",
      state: profile.state || "",
      country: profile.country || "",
      specialization: profile.specialization || "",
      bio: profile.bio || "",
      image: null,
    });
    setEditing(true);
    setSuccess("");
    setError("");
  };

  // Handle profile form changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setEditForm({ ...editForm, image: files[0] });
    } else {
      setEditForm({ ...editForm, [name]: value });
    }
  };

  // Handle profile update
  const handleUpdate = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("You are not logged in.");
        return;
      }
      const formData = new FormData();
      Object.entries(editForm).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyer/profile`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        // Refetch profile from API to get updated fields
        const profileRes = await fetch(`${process.env.REACT_APP_API_URL}/api/lawyer/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        const profileData = await profileRes.json();
        setProfile(profileData.profile || profileData);
        setEditing(false);
        setSuccess("Profile updated successfully!");
      } else {
        setError(data.message || "Failed to update profile.");
      }
    } catch {
      setError("Network error.");
    }
  };

  // Responsive image src
  const profileImageSrc = profile?.image
    ? (() => {
        const isAbsoluteUrl =
          profile.image.startsWith("https://") || profile.image.startsWith("http://");
        return isAbsoluteUrl
          ? profile.image
          : `${process.env.REACT_APP_API_URL.replace(/\/$/, "")}${profile.image}`;
      })()
    : "";

  let content;
  if (loading) {
    content = <div>Loading...</div>;
  } else if (error) {
    content = <div className="text-red-600">{error}</div>;
  } else {
    content = (
      <div className="flex flex-col gap-8">
        {/* Profile Section */}
        {profile && (
          <div className="mb-6 bg-white rounded shadow p-4 max-w-2xl w-full mx-auto">
            <h3 className="text-lg font-semibold mb-2">Profile</h3>
            {editing ? (
              <form onSubmit={handleUpdate} className="flex flex-col gap-2" encType="multipart/form-data">
                <input type="text" name="name" value={editForm.name} onChange={handleChange} placeholder="Name" className="border rounded px-2 py-1" required />
                <input type="text" name="phone" value={editForm.phone} onChange={handleChange} placeholder="Phone" className="border rounded px-2 py-1" />
                <input type="text" name="address" value={editForm.address} onChange={handleChange} placeholder="Address" className="border rounded px-2 py-1" />
                <input type="text" name="city" value={editForm.city} onChange={handleChange} placeholder="City" className="border rounded px-2 py-1" />
                <input type="text" name="state" value={editForm.state} onChange={handleChange} placeholder="State" className="border rounded px-2 py-1" />
                <input type="text" name="country" value={editForm.country} onChange={handleChange} placeholder="Country" className="border rounded px-2 py-1" />
                <input type="text" name="specialization" value={editForm.specialization} onChange={handleChange} placeholder="Specialization" className="border rounded px-2 py-1" />
                <textarea name="bio" value={editForm.bio} onChange={handleChange} placeholder="Bio" className="border rounded px-2 py-1" />
                <input type="file" name="image" accept="image/*" onChange={handleChange} className="border rounded px-2 py-1" />
                <div className="flex gap-2 mt-2">
                  <button type="submit" className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-800">Save</button>
                  <button type="button" className="bg-gray-400 text-white px-3 py-1 rounded hover:bg-gray-600" onClick={() => setEditing(false)}>Cancel</button>
                </div>
              </form>
            ) : (
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div>Name: {profile.name}</div>
                  <div>Email: {profile.email}</div>
                  <div>Phone: {profile.phone || "N/A"}</div>
                  <div>Address: {profile.address || "N/A"}</div>
                  <div>City: {profile.city || "N/A"}</div>
                  <div>State: {profile.state || "N/A"}</div>
                  <div>Country: {profile.country || "N/A"}</div>
                  <div>Specialization: {profile.specialization || "N/A"}</div>
                  <div>Bio: {profile.bio || "N/A"}</div>
                  <button className="mt-2 bg-blue-700 text-white px-4 py-1 rounded hover:bg-blue-800" onClick={handleEdit}>
                    Edit Profile
                  </button>
                  {success && <div className="text-green-600 mt-2">{success}</div>}
                </div>
                {profile.image && (
                  <div>
                    <img
                      src={profileImageSrc}
                      alt="Profile"
                      className="h-24 w-24 object-cover rounded-full mt-2 border"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Request to Client Modal */}
        {showRequestModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
              <h3 className="text-lg font-bold mb-2">Send Request to Client</h3>
              <form onSubmit={handleRequestSubmit} className="flex flex-col gap-2">
                <select
                  name="client_id"
                  value={requestForm.client_id}
                  onChange={handleRequestChange}
                  required
                  className="border rounded px-2 py-1"
                >
                  <option value="">Select Client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name} ({client.email})
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  name="title"
                  value={requestForm.title}
                  onChange={handleRequestChange}
                  placeholder="Title"
                  className="border rounded px-2 py-1"
                  required
                />
                <input
                  type="file"
                  name="documents"
                  multiple
                  onChange={handleRequestFileChange}
                  className="border rounded px-2 py-1"
                />
                <textarea
                  name="message"
                  value={requestForm.message}
                  onChange={handleRequestChange}
                  placeholder="Message"
                  className="border rounded px-2 py-1"
                />
                <div className="flex gap-2 mt-2">
                  <button type="submit" className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-800">
                    Send
                  </button>
                  <button
                    type="button"
                    className="bg-gray-400 text-white px-3 py-1 rounded hover:bg-gray-600"
                    onClick={() => setShowRequestModal(false)}
                  >
                    Cancel
                  </button>
                </div>
                {requestError && <div className="text-red-600">{requestError}</div>}
                {requestSuccess && <div className="text-green-600">{requestSuccess}</div>}
              </form>
            </div>
          </div>
        )}

        {/* Requests Table */}
        <div className="w-full max-w-4xl mx-auto">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-800 mb-4"
            onClick={() => setShowRequestModal(true)}
          >
            Request to Client
          </button>
          <h3 className="text-lg font-semibold mb-2">Assigned/Available Requests</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded shadow text-sm">
              <thead>
                <tr>
                  <th className="px-4 py-2 border">Title</th>
                  <th className="px-4 py-2 border">Status</th>
                  <th className="px-4 py-2 border">Client</th>
                  <th className="px-4 py-2 border">Created</th>
                  <th className="px-4 py-2 border">Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.length > 0 ? (
                  requests.map((req) => (
                    <tr key={req.id}>
                      <td className="px-4 py-2 border">{req.title}</td>
                      <td className="px-4 py-2 border">{req.status}</td>
                      <td className="px-4 py-2 border">{req.client_name || "N/A"}</td>
                      <td className="px-4 py-2 border">{new Date(req.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-2 border">
                        <button
                          className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-800"
                          onClick={() => navigate(`/requests/${req.id}/?type=lawyer`)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      No requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <h2 className="text-2xl font-bold mb-4">Lawyer Dashboard</h2>
      {content}
    </div>
  );
}

